import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Video, Monitor, MonitorSmartphone, Upload, Smartphone, Loader2, Circle,
  Square, Trash2, Plus, CheckCircle2, RefreshCw, Captions, Wand2, Pencil, AlertTriangle,
  ScrollText, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { TeleprompterOverlay } from './TeleprompterOverlay';
import { Phase4Panel, defaultBranding } from './Phase4Panel';
import {
  CAPTION_PRESETS, getPreset, buildGroups, drawCaptionFrame, alignEdit,
} from '../lib/captionPresets';
import { extractAudio } from '../lib/videoRender';
import { useVideoRender } from '../context/VideoRenderContext';
import { getVideoCapabilities } from '../lib/videoSupport';

const FORMAT_DIMS = {
  '9:16': [1080, 1920],
  '16:9': [1920, 1080],
  '1:1': [1080, 1080],
  '4:5': [1080, 1350],
};

/**
 * AI Video Studio — Phase 1 (Capture & Attach) + Phase 2 (Captions).
 * Record / upload / record-from-phone, trim, auto-caption (Whisper word
 * timestamps) with 6 animated presets + inline text editing, then burn the
 * captions in via canvas + MediaRecorder (browser, $0 server compute) and
 * attach the final clip to the campaign post.
 */
export const VideoStudioModal = ({ post, projectId, initialScript, open: openProp, onClose, onAttached, onSaved }) => {
  const { api } = useAuth();
  const { enqueue } = useVideoRender();
  const open = openProp !== undefined ? openProp : !!post;
  const caps = getVideoCapabilities();

  const [project, setProject] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordKind, setRecordKind] = useState(null);
  const [clip, setClip] = useState(null); // { blob, url, duration }
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [format, setFormat] = useState('9:16');
  const [mobile, setMobile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');
  const [posts, setPosts] = useState([]);
  const [targetPostId, setTargetPostId] = useState('');

  // Captions (Phase 2)
  const [words, setWords] = useState(null);        // working words [{word,start,end}]
  const origWordsRef = useRef(null);               // pristine transcript for re-alignment
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionPreset, setCaptionPreset] = useState('bold_pop');
  const [transcribing, setTranscribing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showEditor, setShowEditor] = useState(false);

  // Teleprompter / Script (P1)
  const [scriptOpen, setScriptOpen] = useState(false);
  const [scriptTopic, setScriptTopic] = useState('');
  const [scriptTone, setScriptTone] = useState('energetic');
  const [scriptDuration, setScriptDuration] = useState('30s');
  const [script, setScript] = useState('');
  const [scriptMode, setScriptMode] = useState('ai'); // 'ai' = write with AI | 'own' = paste your own
  const [generatingScript, setGeneratingScript] = useState(false);
  const [teleprompterOn, setTeleprompterOn] = useState(true);
  const [branding, setBranding] = useState(defaultBranding());
  const [preparing, setPreparing] = useState(false);   // camera on, counting down
  const [countdown, setCountdown] = useState(null);     // 3 | 2 | 1 | null

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamsRef = useRef([]);
  const rafRef = useRef(null);
  const videoElRef = useRef(null);
  const liveVideoRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset so reopening always starts a fresh project.
      setProject(null); setClip(null); setMobile(null);
      setWords(null); setCaptionsOn(false); setShowEditor(false);
      setPosts([]); setTargetPostId('');
      setScript(''); setScriptOpen(false); setScriptTopic(''); setScriptMode('ai'); setRecordKind(null);
      setBranding(defaultBranding());
      cleanupStreams();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        let proj;
        if (projectId) {
          const r = await api.get(`/video/projects/${projectId}`);
          proj = r.data;
        } else {
          const body = post ? { campaign_post_id: post.id, name: post.post_title || 'Video' } : { name: 'Untitled video' };
          const r = await api.post('/video/projects', body);
          proj = r.data;
        }
        if (cancelled) return;
        setProject(proj);
        // Editing an existing draft: load the latest source clip so the user
        // can continue trimming / captioning / exporting.
        const last = (proj.source_media || []).slice(-1)[0];
        if (projectId && last?.url) {
          const url = last.url.startsWith('http') ? last.url : `${process.env.REACT_APP_BACKEND_URL}${last.url}`;
          setClip({ url, remoteMedia: last, duration: last.duration || 0 });
          setTrimStart(0);
          setTrimEnd(last.duration || 0);
          // Probe real duration for reliable trim/export when it wasn't stored.
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.onloadedmetadata = () => {
            const dur = isFinite(v.duration) && v.duration > 0 ? v.duration : (last.duration || 0);
            if (dur > 0) {
              setClip((c) => (c ? { ...c, duration: dur } : c));
              setTrimEnd((te) => te || dur);
            }
          };
          v.src = url;
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not start Video Studio');
        onClose();
      }
    })();
    setTargetPostId(post?.id || '');
    setScriptTopic(post?.post_title || post?.topic || '');
    if (initialScript && initialScript.trim()) { setScript(initialScript.trim()); setScriptOpen(true); }
    api.get('/posts?limit=200').then((r) => { if (!cancelled) setPosts(r.data.posts || []); }).catch(() => {});
    return () => { cancelled = true; cleanupStreams(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset caption state whenever a new clip is loaded.
  useEffect(() => {
    setWords(null);
    origWordsRef.current = null;
    setCaptionsOn(false);
    setShowEditor(false);
    setEditText('');
  }, [clip?.url]);

  // Live caption preview overlay — mirrors the burn-in renderer exactly.
  useEffect(() => {
    if (!captionsOn || !words?.length) return undefined;
    const preset = getPreset(captionPreset);
    const groups = buildGroups(words, preset.maxWords);
    let raf;
    const render = () => {
      const cvs = previewCanvasRef.current;
      const vid = videoElRef.current;
      if (cvs && vid) {
        const W = vid.clientWidth;
        const H = vid.clientHeight;
        if (W && H) {
          if (cvs.width !== W) cvs.width = W;
          if (cvs.height !== H) cvs.height = H;
          const ctx = cvs.getContext('2d');
          ctx.clearRect(0, 0, W, H);
          drawCaptionFrame(ctx, groups, vid.currentTime, preset, W, H);
        }
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [captionsOn, words, captionPreset]);

  // Bind the live webcam stream to the in-modal preview while recording so the
  // presenter can see themselves with the teleprompter overlaid.
  useEffect(() => {
    if ((recording || preparing) && recordKind === 'recording' && liveVideoRef.current) {
      const s = streamsRef.current[0];
      if (s) {
        liveVideoRef.current.srcObject = s;
        liveVideoRef.current.play().catch(() => {});
      }
    }
  }, [recording, preparing, recordKind]);

  const cleanupStreams = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    streamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streamsRef.current = [];
    setPreparing(false);
    setCountdown(null);
  };

  // ----------------------------------------------------------- recording
  const pickRecordMime = () => {
    const candidates = [
      'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus',
      'video/webm', 'video/mp4',
    ];
    for (const c of candidates) {
      try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (_) { /* noop */ }
    }
    return '';
  };

  const beginRecorder = (stream) => {
    chunksRef.current = [];
    const mime = pickRecordMime();
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const outType = rec.mimeType || mime || 'video/webm';
      const blob = new Blob(chunksRef.current, { type: outType });
      const url = URL.createObjectURL(blob);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        const dur = isFinite(v.duration) ? v.duration : 0;
        setClip({ blob, url, duration: dur });
        setTrimStart(0); setTrimEnd(dur);
      };
      v.src = url;
      cleanupStreams();
      setRecording(false);
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
  };

  const runCountdown = () => new Promise((resolve) => {
    let n = 3;
    setCountdown(n);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        setCountdown(null);
        resolve();
      } else {
        setCountdown(n);
      }
    }, 1000);
  });

  const startRecording = async (kind) => {
    try {
      cleanupStreams();
      setRecordKind(kind);
      if (kind === 'recording') {
        const videoConstraints = caps.isMobile ? { facingMode: 'user' } : { width: 1280, height: 720 };
        const s = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: true });
        streamsRef.current = [s];
        setPreparing(true);
        await runCountdown();
        // User may have closed the modal / cancelled during the countdown.
        if (!streamsRef.current.length) return;
        setPreparing(false);
        beginRecorder(s);
      } else if (kind === 'screen') {
        const s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        streamsRef.current = [s];
        s.getVideoTracks()[0].onended = () => stopRecording();
        beginRecorder(s);
      } else if (kind === 'webcam_screen') {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        const cam = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 }, audio: true });
        streamsRef.current = [screen, cam];
        const sv = document.createElement('video'); sv.srcObject = screen; sv.muted = true; await sv.play();
        const cv = document.createElement('video'); cv.srcObject = cam; cv.muted = true; await cv.play();
        const canvas = document.createElement('canvas');
        canvas.width = 1280; canvas.height = 720;
        const ctx = canvas.getContext('2d');
        const draw = () => {
          ctx.drawImage(sv, 0, 0, canvas.width, canvas.height);
          const pw = canvas.width * 0.25, ph = pw * (cv.videoHeight / cv.videoWidth || 0.75);
          ctx.drawImage(cv, canvas.width - pw - 20, canvas.height - ph - 20, pw, ph);
          rafRef.current = requestAnimationFrame(draw);
        };
        draw();
        const canvasStream = canvas.captureStream(30);
        const audioCtx = new AudioContext();
        const dest = audioCtx.createMediaStreamDestination();
        [cam, screen].forEach((st) => st.getAudioTracks().length && audioCtx.createMediaStreamSource(st).connect(dest));
        const mixed = new MediaStream([canvasStream.getVideoTracks()[0], ...dest.stream.getAudioTracks()]);
        streamsRef.current.push(canvasStream);
        beginRecorder(mixed);
      }
    } catch (e) {
      toast.error('Recording permission denied or unavailable. Use HTTPS + Chrome/Edge/Safari.');
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
  };

  // ----------------------------------------------------------- upload / clips
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      const dur = isFinite(v.duration) ? v.duration : 0;
      setClip({ blob: file, url, duration: dur, isUpload: true });
      setTrimStart(0); setTrimEnd(dur);
    };
    v.src = url;
  };

  const genMobileLink = async () => {
    if (!project) return;
    try {
      const r = await api.post(`/video/projects/${project.id}/mobile-link`);
      setMobile(r.data);
    } catch (e) {
      toast.error('Could not create mobile link');
    }
  };

  const refreshProject = async () => {
    if (!project) return;
    setBusy(true); setBusyMsg('Checking for new clips…');
    try {
      const r = await api.get(`/video/projects/${project.id}`);
      setProject(r.data);
      const last = (r.data.source_media || []).slice(-1)[0];
      if (last && last.kind === 'mobile') {
        setClip({ url: `${process.env.REACT_APP_BACKEND_URL}${last.url}`, remoteMedia: last, duration: last.duration || 0 });
        setTrimStart(0); setTrimEnd(last.duration || 0);
        toast.success('Phone recording loaded');
      }
    } finally { setBusy(false); setBusyMsg(''); }
  };

  // ----------------------------------------------------------- captions
  const srcBlobFromClip = async () => {
    if (clip.blob) return clip.blob;
    if (clip.url) return (await fetch(clip.url)).blob();
    return null;
  };

  const generateCaptions = async () => {
    if (!clip || !project) return;
    setTranscribing(true); setBusyMsg('Extracting audio…');
    try {
      const srcBlob = await srcBlobFromClip();
      setBusyMsg('Transcribing with Whisper…');
      const audio = await extractAudio(srcBlob, { trimStart, trimEnd, clipDuration: clip.duration });
      const fd = new FormData();
      fd.append('file', new File([audio], 'audio.m4a', { type: 'audio/mp4' }));
      const r = await api.post(`/video/projects/${project.id}/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Whisper timestamps are relative to the trimmed audio, so they already
      // line up with the trimmed clip (which is what we burn in).
      const w = r.data.words || [];
      if (!w.length) {
        toast.error('No speech detected in this clip');
        return;
      }
      origWordsRef.current = w;
      setWords(w);
      setEditText(w.map((x) => x.word).join(' '));
      setCaptionsOn(true);
      toast.success(`Captions generated — ${w.length} words`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not transcribe audio');
    } finally { setTranscribing(false); setBusyMsg(''); }
  };

  const applyTextEdits = () => {
    const aligned = alignEdit(origWordsRef.current || [], editText);
    setWords(aligned);
    setShowEditor(false);
    toast.success('Caption text updated');
  };

  // ----------------------------------------------------------- script (teleprompter)
  const generateScript = async () => {
    if (!scriptTopic.trim()) { toast.error('Add a topic for your script first'); return; }
    setGeneratingScript(true);
    try {
      const r = await api.post('/video/script', {
        topic: scriptTopic.trim(), tone: scriptTone, duration: scriptDuration,
        platform: post?.platform || 'instagram',
      });
      setScript(r.data.script || '');
      setTeleprompterOn(true);
      toast.success(`Script ready — ~${r.data.word_count} words (~${r.data.target_seconds}s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate a script');
    } finally { setGeneratingScript(false); }
  };

  const rephraseScript = async () => {
    if (!script.trim()) return;
    setGeneratingScript(true);
    try {
      const r = await api.post('/video/script', {
        topic: scriptTopic.trim() || 'video', tone: scriptTone, duration: scriptDuration,
        platform: post?.platform || 'instagram', source_text: script.trim(),
      });
      setScript(r.data.script || script);
      toast.success('Rewritten as a spoken script');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rewrite the script');
    } finally { setGeneratingScript(false); }
  };

  // ----------------------------------------------------------- save (background)
  const finishVideo = async (postNow = false) => {
    if (!clip || !project) return;
    setBusy(true);
    setBusyMsg('Preparing…');
    try {
      const srcBlob = await srcBlobFromClip();
      const captioned = captionsOn && words?.length > 0 && caps.canBurnIn;
      const srcUrl = (clip.url && /^https?:/i.test(clip.url)) ? clip.url : null;
      enqueue({
        kind: 'single',
        projectId: project.id,
        srcBlob,
        srcUrl,
        clipDuration: clip.duration,
        format,
        trimStart,
        trimEnd,
        captioned,
        words: captioned ? words : null,
        presetKey: captionPreset,
        overlays: {
          logo: branding.logo,
          progressBar: branding.progressBar,
          lowerThird: branding.lowerThird,
          outro: branding.outro,
        },
        musicUrl: branding.music.url || null,
        musicVolume: branding.music.volume,
        duckMain: branding.duckMain,
        voiceoverUrl: branding.voiceover.url || null,
        targetPostId: postNow ? '' : targetPostId,
        postNowAfter: postNow,
        label: postNow ? 'Rendering to post' : (targetPostId ? 'Saving & attaching video' : 'Saving video'),
      });
      toast.success(postNow
        ? 'Rendering — the post options will open when it\'s ready'
        : 'Rendering in the background — you can keep working');
      onAttached?.();
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error('Could not start the render');
    } finally { setBusy(false); setBusyMsg(''); }
  };

  const recordBtns = [
    { kind: 'recording', label: 'Webcam', icon: Video },
    { kind: 'screen', label: 'Screen', icon: Monitor },
    { kind: 'webcam_screen', label: 'Webcam + Screen', icon: MonitorSmartphone },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="video-studio-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-indigo-600" /> Video Studio</DialogTitle>
          <DialogDescription className="sr-only">Record, write a teleprompter script, caption, and export social videos.</DialogDescription>
        </DialogHeader>

        {!project ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-indigo-600" /></div>
        ) : (
          <div className="space-y-5">
            {(caps.isMobile ? !caps.canBurnIn : (!caps.canRecord || !caps.canBurnIn)) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="browser-support-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-[11px] text-amber-800 leading-relaxed">
                  {caps.isMobile ? (
                    <>On this device, caption <strong>burn-in</strong> isn’t available — your video saves/attaches without burned-in captions. Recording with your camera and uploads work fully. For captioned exports, use <strong>Chrome or Edge on desktop</strong>.</>
                  ) : !caps.canRecord ? (
                    <>This browser can’t record or burn in captions{caps.isSafari ? ' (Safari limitation)' : ''}. You can still <strong>upload a video</strong> or <strong>record on your phone</strong> (QR). For full features, use <strong>Chrome or Edge on desktop</strong>.</>
                  ) : (
                    <>Caption burn-in isn’t supported in this browser — videos will attach without burned-in captions. Use <strong>Chrome or Edge</strong> for captioned exports.</>
                  )}
                </div>
              </div>
            )}
            {/* Script & Teleprompter (P1) */}
            <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="script-panel">
              <button
                type="button"
                onClick={() => setScriptOpen((s) => !s)}
                className="w-full flex items-center justify-between gap-2"
                data-testid="script-panel-toggle"
              >
                <Label className="text-xs flex items-center gap-1.5 cursor-pointer">
                  <ScrollText className="w-4 h-4 text-indigo-600" /> Teleprompter script
                  {script && <span className="text-[10px] text-emerald-600 font-medium">· ready</span>}
                </Label>
                <span className="text-[11px] text-indigo-600">{scriptOpen ? 'Hide' : 'Write a script'}</span>
              </button>

              {scriptOpen && (
                <div className="space-y-3">
                  {/* Mode: write with AI vs paste an existing script */}
                  <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg" data-testid="script-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setScriptMode('ai')}
                      className={`flex items-center justify-center gap-1.5 text-[11px] font-medium py-1.5 rounded-md transition-colors ${scriptMode === 'ai' ? 'bg-white text-indigo-600 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                      data-testid="script-mode-ai"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Write with AI
                    </button>
                    <button
                      type="button"
                      onClick={() => setScriptMode('own')}
                      className={`flex items-center justify-center gap-1.5 text-[11px] font-medium py-1.5 rounded-md transition-colors ${scriptMode === 'own' ? 'bg-white text-indigo-600 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                      data-testid="script-mode-own"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Paste your own
                    </button>
                  </div>

                  {scriptMode === 'ai' && (
                    <>
                      <Textarea
                        value={scriptTopic}
                        onChange={(e) => setScriptTopic(e.target.value)}
                        rows={2}
                        className="text-xs"
                        placeholder="What's the video about? e.g. 3 quick tips to grow on Instagram"
                        data-testid="script-topic-input"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[11px]">Tone</Label>
                          <Select value={scriptTone} onValueChange={setScriptTone}>
                            <SelectTrigger data-testid="script-tone"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['energetic', 'professional', 'casual', 'inspirational', 'funny', 'educational'].map((t) => (
                                <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px]">Length</Label>
                          <Select value={scriptDuration} onValueChange={setScriptDuration}>
                            <SelectTrigger data-testid="script-duration"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['15s', '30s', '60s', '90s'].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={generateScript} disabled={generatingScript} data-testid="generate-script-btn">
                        {generatingScript ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                        {generatingScript ? 'Writing…' : (script ? 'Regenerate script' : 'Generate script')}
                      </Button>
                    </>
                  )}

                  {(scriptMode === 'own' || script) && (
                    <>
                      {scriptMode === 'own' && (
                        <Label className="text-[11px] text-zinc-500">Paste the script you've already written below.</Label>
                      )}
                      <Textarea
                        value={script}
                        onChange={(e) => setScript(e.target.value)}
                        rows={scriptMode === 'own' ? 7 : 5}
                        className="text-xs leading-relaxed"
                        placeholder={scriptMode === 'own' ? 'Paste your existing script here…' : 'Your script…'}
                        data-testid="script-text"
                      />
                      <Button size="sm" variant="ghost" onClick={rephraseScript} disabled={generatingScript || !script.trim()} className="text-indigo-600 h-7 px-2" data-testid="make-spoken-btn">
                        <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Make it spoken
                      </Button>
                      <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer">
                        <input type="checkbox" checked={teleprompterOn} onChange={(e) => setTeleprompterOn(e.target.checked)} className="accent-indigo-600" data-testid="teleprompter-toggle" />
                        Show teleprompter over the camera while recording
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Capture */}
            <div>
              <Label className="text-xs">Record</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {caps.isMobile ? (
                  <>
                    {caps.canRecord && !recording && !preparing && (
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={() => startRecording('recording')} data-testid="mobile-record-teleprompter">
                        <Video className="w-4 h-4 mr-1.5" /> {script && teleprompterOn ? 'Record with teleprompter' : 'Record in app'}
                      </Button>
                    )}
                    {recording && (
                      <Button size="sm" variant="destructive" onClick={stopRecording} data-testid="stop-recording">
                        <Square className="w-4 h-4 mr-1.5" /> Stop
                      </Button>
                    )}
                    {!recording && !preparing && (
                      <span className="relative inline-flex" data-testid="mobile-camera-label">
                        <span className={`inline-flex items-center text-sm px-3 py-1.5 rounded-md ${caps.canRecord ? 'border border-zinc-200 text-zinc-700 hover:border-zinc-300' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                          <Video className="w-4 h-4 mr-1.5" /> {caps.canRecord ? 'Native camera' : 'Record with camera'}
                        </span>
                        <input
                          type="file"
                          accept="video/*"
                          capture="environment"
                          onChange={onUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          aria-label="Record with native camera"
                          data-testid="mobile-camera-input"
                        />
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {recordBtns.map((b) => (
                      <Button key={b.kind} variant="outline" size="sm" disabled={recording || preparing || !caps.canRecord}
                        onClick={() => startRecording(b.kind)} data-testid={`record-${b.kind}`}>
                        <b.icon className="w-4 h-4 mr-1.5" /> {b.label}
                      </Button>
                    ))}
                    {recording && (
                      <Button size="sm" variant="destructive" onClick={stopRecording} data-testid="stop-recording">
                        <Square className="w-4 h-4 mr-1.5" /> Stop
                      </Button>
                    )}
                  </>
                )}
                <label className="inline-flex items-center text-sm px-3 py-1.5 rounded-md border border-zinc-200 cursor-pointer hover:border-zinc-300" data-testid="video-upload-label">
                  <Upload className="w-4 h-4 mr-1.5" /> Upload
                  <input type="file" accept="video/*" className="hidden" onChange={onUpload} data-testid="video-upload-input" />
                </label>
                {!caps.isMobile && (
                  <Button variant="outline" size="sm" onClick={genMobileLink} data-testid="mobile-link-btn">
                    <Smartphone className="w-4 h-4 mr-1.5" /> Record on phone
                  </Button>
                )}
              </div>
              {recording && <p className="text-xs text-rose-600 mt-2 flex items-center gap-1.5 animate-pulse"><Circle className="w-3 h-3 fill-rose-600" /> Recording…</p>}
              {preparing && <p className="text-xs text-indigo-600 mt-2">Get ready — recording starts after the countdown…</p>}
            </div>

            {/* Mobile QR */}
            {mobile && (
              <div className="rounded-lg border border-zinc-200 p-3 flex items-center gap-4" data-testid="mobile-qr">
                <div className="bg-white p-2 rounded"><QRCodeCanvas value={mobile.url} size={96} /></div>
                <div className="flex-1 text-xs text-zinc-600">
                  <p className="font-medium text-zinc-800">Scan with your phone to record</p>
                  <p className="break-all text-[11px] text-zinc-400 mt-1">{mobile.url}</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={refreshProject} data-testid="refresh-clips">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> I've finished — load my clip
                  </Button>
                </div>
              </div>
            )}

            {/* Live webcam preview + countdown + teleprompter while recording */}
            {(recording || preparing) && recordKind === 'recording' && (
              <div className="relative w-full" data-testid="live-preview">
                <video ref={liveVideoRef} muted playsInline className="w-full rounded-lg border border-zinc-200 max-h-[360px] bg-black" />
                {recording && script && teleprompterOn && <TeleprompterOverlay text={script} />}
                {countdown != null && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/45 rounded-lg pointer-events-none" data-testid="record-countdown">
                    <span key={countdown} className="text-white font-bold text-8xl" style={{ textShadow: '0 4px 16px rgba(0,0,0,0.7)' }}>
                      {countdown}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Preview + trim + format */}
            {clip ? (
              <div className="space-y-3" data-testid="video-preview">
                <div className="relative w-full">
                  <video ref={videoElRef} src={clip.url} controls className="w-full rounded-lg border border-zinc-200 max-h-[320px] bg-black" />
                  {captionsOn && words?.length > 0 && (
                    <canvas ref={previewCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none rounded-lg" data-testid="caption-preview-canvas" />
                  )}
                </div>
                {clip.duration > 0 && (
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <Label className="text-[11px]">Trim start: {trimStart.toFixed(1)}s</Label>
                      <input type="range" min={0} max={clip.duration} step={0.1} value={trimStart}
                        onChange={(e) => setTrimStart(Math.min(Number(e.target.value), trimEnd))} className="w-full accent-indigo-600" data-testid="trim-start" />
                    </div>
                    <div>
                      <Label className="text-[11px]">Trim end: {trimEnd.toFixed(1)}s</Label>
                      <input type="range" min={0} max={clip.duration} step={0.1} value={trimEnd}
                        onChange={(e) => setTrimEnd(Math.max(Number(e.target.value), trimStart))} className="w-full accent-indigo-600" data-testid="trim-end" />
                    </div>
                  </div>
                )}

                {/* Captions */}
                <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="captions-panel">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs flex items-center gap-1.5"><Captions className="w-4 h-4 text-indigo-600" /> Captions</Label>
                    {!words?.length ? (
                      <Button size="sm" variant="outline" onClick={generateCaptions} disabled={transcribing} data-testid="generate-captions-btn">
                        {transcribing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                        {transcribing ? 'Transcribing…' : 'Auto-generate'}
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setShowEditor((s) => !s)} data-testid="edit-captions-btn">
                          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit text
                        </Button>
                        <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
                          <input type="checkbox" checked={captionsOn} disabled={!caps.canBurnIn} onChange={(e) => setCaptionsOn(e.target.checked)} className="accent-indigo-600" data-testid="captions-toggle" />
                          Burn in
                        </label>
                      </div>
                    )}
                  </div>

                  {words?.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-2" data-testid="caption-presets">
                        {CAPTION_PRESETS.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => { setCaptionPreset(p.key); setCaptionsOn(true); }}
                            className={`text-left rounded-md border p-2 transition-colors ${captionPreset === p.key ? 'border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50/40' : 'border-zinc-200 hover:border-zinc-300'}`}
                            data-testid={`caption-preset-${p.key}`}
                          >
                            <div className="flex items-center gap-1 mb-1">
                              {p.swatch.map((c, i) => <span key={i} className="w-3 h-3 rounded-full border border-black/10" style={{ background: c }} />)}
                            </div>
                            <p className="text-[11px] font-semibold text-zinc-800 leading-tight">{p.name}</p>
                            <p className="text-[10px] text-zinc-500 leading-tight">{p.desc}</p>
                          </button>
                        ))}
                      </div>

                      {showEditor && (
                        <div className="space-y-2" data-testid="caption-editor">
                          <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            rows={3}
                            className="text-xs"
                            placeholder="Edit the transcript…"
                            data-testid="caption-text-input"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={applyTextEdits} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apply-caption-edits-btn">Apply edits</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditText((origWordsRef.current || []).map((w) => w.word).join(' ')); }}>Reset</Button>
                          </div>
                        </div>
                      )}
                      <p className="text-[11px] text-zinc-400">Captions are word-synced and burned into the exported video. Play the preview to see them.</p>
                    </>
                  )}
                </div>

                <Phase4Panel value={branding} onChange={setBranding} api={api} scriptText={script || (words || []).map((w) => w.word).join(' ')} />

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px]">Export format</Label>
                      <Select value={format} onValueChange={setFormat}>
                        <SelectTrigger data-testid="video-format"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(FORMAT_DIMS).map((f) => <SelectItem key={f} value={f}>{f}{f === '9:16' ? ' (Reels/TikTok)' : f === '4:5' ? ' (Feed)' : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Attach to campaign post</Label>
                      <Select value={targetPostId || '__none__'} onValueChange={(v) => setTargetPostId(v === '__none__' ? '' : v)}>
                        <SelectTrigger data-testid="attach-post-picker"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="__none__">Save to library only</SelectItem>
                          {posts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {(p.post_title || p.topic || 'Untitled').slice(0, 38)} · {p.platform}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => finishVideo(false)} disabled={busy} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="attach-video-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                      {targetPostId ? 'Save & attach to post' : 'Save to library'}
                    </Button>
                    <Button onClick={() => finishVideo(true)} disabled={busy} variant="outline" className="flex-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="save-post-now-btn">
                      <Send className="w-4 h-4 mr-1.5" /> Save &amp; post now
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setClip(null)} data-testid="discard-clip"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-[11px] text-zinc-400">Renders in your browser — best for clips under ~90s. The first render loads a one-time engine (~25&nbsp;MB); keep this tab open until it finishes.</p>
                </div>
                {busy && busyMsg && <p className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {busyMsg}</p>}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-400" data-testid="video-empty">
                Record, upload, or use your phone — your clip appears here for trimming, captions &amp; export.
              </div>
            )}

            {project.source_media?.length > 0 && (
              <p className="text-[11px] text-zinc-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> {project.source_media.length} source clip(s) saved (originals preserved).</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoStudioModal;
